import moment from 'moment'
import * as React from 'react'
import { useEffect, useState, useRef, useCallback} from "react"
import { useHistory } from 'react-router-dom'
import ReactJson from "react-json-view"
import { useDispatch, useSelector } from "react-redux"
import * as globals from '../../globals'
import { actionCreators as modelActionCreator } from '../../store/Model'
import { OPTIM_UPDATE_RUNITEM } from '../../store/action-types.ts'
import * as apiService from '../../utils/apiService'
import './OptRunDrawer.scss'
import { format as d3Format } from 'd3-format'
import {
	Bar,
	BarChart,
	LabelList,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"


export const OptRunItem = (props) => {

    const runId = props.runId
    const refreshFunc = props.refreshParent
    const scnLoadPath = props?.scnLoadPath

    const run = useSelector(state => state.optimizer?.optRunDict?.[runId])
    const state = useSelector(state => state)
    const modelConfig = state?.model?.activeConfig || 'dcm_ml_truck_2024'
    const atts = state.model[modelConfig]?.config.productDef?.atts
    const studyId = useSelector(state => state.study?.uid)
    const auth = useSelector(state => state.auth)
    const dispatch = useDispatch()
    const history = useHistory()

    const [loading, setLoading] = useState(false)
    const [refreshCounter, setRefreshCounter] = useState(0)
    const [runOutput, setRunOutput] = useState(null)
    const [runInput, setRunInput] = useState(null)
    const [selectedHistoryEntry, setSelectedHistoryEntry] = useState(null)
    const [selectedBarData, setSelectedBarData] = useState(null)
    const [expanded, setExpanded] = useState(false)
    const intervalRef = useRef(null)
    const runStateRef = useRef(run?.state)
    const isInitialExpand = useRef(false)

    console.log('rendering OptRunItem component')
        
    useEffect(() => {
        if (run && run.state !== runStateRef.current) {
            console.log('Updating ref with state:', run.state)
            runStateRef.current = run.state // Always keep ref in sync with the latest state
        }
    }, [run])

    // Fetch data from the API and dispatch to Redux
   const fetchData = useCallback(async () => {
        try {
            const response = await apiService.aGet(auth, `${globals.apiRoot}/optimize/run/${runId}`)
            if (response.ok) {
                const data = await response.json()
                setRunOutput(data?.output)
                setRunInput(data?.input)

                console.log('DATA OUTPUT STATE!!!!!!!!!!!!!!!!!!!!', data?.state);
                // Ensure that the latest state from the backend is reflected in your component
                if (data.state === 'completed') {
                    console.log('Run is completed, stopping updates.')
                    clearInterval(intervalRef.current) // Stop the interval if completed
                    setLoading(false)

                    intervalRef.current = null
                    if (!isInitialExpand.current) {
                        refreshFunc()
                    }
                }
                else {
                    dispatch({ type: OPTIM_UPDATE_RUNITEM, id: runId, data }) // Keep Redux in sync
                }
            }
        } catch (err) {
            console.error('Error fetching data:', err)
        }
    }, [auth, runId])


    // Manage the interval for checking run state
    useEffect(() => {
        if (expanded) {
            // Fetch data initially when expanded
            isInitialExpand.current = true
            fetchData()

            intervalRef.current = setInterval(() => {
                if (run.state !== 'completed') {
                    setLoading(true)
                    fetchData()
                } else {
                    clearInterval(intervalRef.current)
                    setLoading(false)
                }
            }, 5000) // Poll every 5 seconds
        }
        else {
            isInitialExpand.current = false
            clearInterval(intervalRef.current)
            setLoading(false)
        }

        return () => clearInterval(intervalRef.current)
    }, [expanded])


    const getTimestampCell = (run) => {
        //return <div className='timestamp'>{getTimestampMessage(run)}</div>
        if (run.completed) {
            return <div className='timestamp completed'>
                {"Completed " + moment.utc(run.completed).local().fromNow()}
            </div>
        }
        if (run.started) {
            const started = moment.utc(run.started).local()
            const seconds = moment().diff(started, 'seconds')
            const dur = moment.duration(seconds, 'seconds').humanize()
            return <div className='timestamp running'>
                {"Running for " + dur}
            </div>
        }
        {
            const created = moment.utc(run.created).local()
            const seconds = moment().diff(created, 'seconds')
            const dur = moment.duration(seconds, 'seconds').humanize()
            return <div className='timestamp waiting'>
                {"Waiting for " + dur}
            </div>
        }
    }



    const loadOptimizerScenario = async (auth, studyId, runId, scenarioIndex, dispatch, callback) => {

        if (!modelConfig) {
            if (callback) callback(false, 'model activeConfig not defined.')
        }
        
        try {
            const response = await apiService.aGet(auth, `${globals.apiRoot}/optimize/scenario/${studyId}/${runId}/${scenarioIndex}`)

            const data = await response.json()
            console.log('RECEIVED OPTIMIZED SCENARIO FROM API', data)

            if (response.ok) {
                dispatch(modelActionCreator.setPlayAll(modelConfig, data?.config?.products))
                if(callback) callback(true, 'Optimized product configuration loaded')
            }
            else {
                console.error(response)
                if (callback) callback(false, 'Server error')
            }
        } catch (err) {
            if (callback) callback(false, 'Server error')
        }

    } 


    const handleDeleteClick = async (theRun, event) => {
        event.preventDefault() // Prevent default action
        event.stopPropagation() // Stop event bubbling

        try {
            const response = await apiService.aDelete(auth, `${globals.apiRoot}/optimize/run/${theRun.id}`)
            if (response.ok) {
                topBar.enqueueSnack({
                    id: `deleted_optrun_${theRun.id}`,
                    message: 'Deleted Optimization Run',
                    to: 3000,
                    success: true,                    
                })
                refreshFunc()
            }
        } catch (err) {
            console.error(err)
        }
    }

    const onClickExpand = async () => {
        setExpanded(!expanded)
        await fetchData()
    }

    const handleBarClick = (data) => {
        console.log('here click bar', data?.activePayload[0]?.payload);
        setSelectedBarData(data?.activePayload[0]?.payload) 
    }

    const input = runInput
    const goal = input?.goal

    const running = run.state === "running"
    const metric = globals.metrics.find(m => m.key == goal?.targetMeasure)
    const nbScenarios = run?.output?.scenarios?.length

    const completed = run?.completed
    const created = run?.created

    const completedDate = new Date(completed)
    const createdDate = new Date(created)

    // Get the difference in milliseconds
    const diffInMs = completedDate - createdDate

    // Convert milliseconds to total seconds, minutes, hours, and days
    const totalSeconds = Math.floor(diffInMs / 1000)
    const totalMinutes = Math.floor(totalSeconds / 60)
    const totalHours = Math.floor(totalMinutes / 60)
    const totalDays = Math.floor(totalHours / 24)

    // Get remaining hours, minutes, and seconds
    const hours = totalHours % 24
    const minutes = totalMinutes % 60
    const seconds = totalSeconds % 60

    let optSetup = {}
    goal?.targetProducts?.forEach(tp => {
        let tpObj = { ...tp }
        optSetup[tp.label] = tpObj
    })

    optSetup = {
        goal: input?.goal,
        optimizerSettings: input?.all?.optimizerSettings,
        bestScenario: runOutput,
        runTime: `${totalDays > 0 ? totalDays + ' days ' : ''}${hours > 0 || totalDays > 0 ? hours + ' hours ' : ''}${minutes > 0 || hours > 0 || totalDays > 0 ? minutes + ' minutes' : ''}`.trim()

        //input
    }

    console.log('runOutput', runOutput);
    const uniqueResults = Array.from(
        new Map(
            runOutput?.bestResultHistory
            ?.filter(f => f.value > 0) // Keep only positive values
            ?.map(item => [item.value, item]) // Map each value to its item
        ).values() // Extract only the unique items
    )
    
    const chartData = uniqueResults?.map((item, index) => ({ //run.output
        ...item,
        generationIndex: index + 1,
    }))

    // console.log('cahrtData', chartData);
    // console.log('metric', metric);
    
    return (
        <div className='optrun' key={run.id + "|" + refreshCounter}>
            <div className='expander' onClick={() => {
                onClickExpand()
                
            }}>
                <i className={expanded ? 'fal fa-minus-circle' : 'fal fa-plus-circle'} />
                {run.label || 'missing label'}
            </div>
            <div className='state'>{run.state}</div>
            <div className='label'>{run.label || 'missing label'}</div>
            {getTimestampCell(run)}
            <div className='actions' title='Delete Run' onClick={(ev) => handleDeleteClick(run, ev)}>
                <i className='fal fa-trash-alt' />
            </div>
            {expanded ? <div className='run-detail'>
                { <>                    
                    <div className='goal'>
                        <div className="target-measure goal-item">
                            <span className="label">Target measure</span>
                            <span className="value">{metric?.label || goal?.targetMeasure}</span>
                            {/* <span className='btn inline-block' onClick={() => setRefreshCounter(refreshCounter + 1)}>
                                <i className='fal fa-sync'/>
                            </span> */}
                        </div>
                        <div className="target-products goal-item">
                            <span className="label">Target Product(s)</span>
                            <span className="value">
                                {goal?.targetProducts?.map(p => <div key={p.uid} className='entry'>
                                    {p.label}
                                </div>)}
                            </span>
                        </div>
                        <div className="target-filter goal-item">
                            <span className="label">Target filter</span>
                            <span className="value">{goal?.targetFilterLabel}</span>
                        </div>

                        {
                            auth?.user?.isSuperAdmin &&
                            <div className="input goal-item dash">
                                <span className="label">Optimizer Setup Detail (hide me)</span>
                                <span className="value" style={{ textAlign: "left", border: '0px solid #cfcfcf', fontSize: 10 }}>
                                    <ReactJson
                                        src={optSetup || {}}
                                        theme={"rjv-default"}
                                        iconStyle="circle"
                                        displayDataTypes={false}
                                        name={null}
                                        collapsed={0}
                                        enableClipboard={true}
                                    />
                                </span>
                            </div>
                        }

                        <div className="actions">
                            <div className='status-section'>
                                 {loading ? <div className="spinner">
                                        <i className="fas fa-spinner fa-spin"></i> Running Optimization
                                    </div> : null
                                } {/* Spinner */}
                                {run.message ?
                                    <div className='status-message'>
                                        {`${run.message} - Completed in ${optSetup.runTime}`}
                                    </div>
                                    : null}
                                {run.hasOutput ? <div className='best-result-section' style={{ display: 'flex' }}>
                                     {/* Column 1 - Empty */}
                                    <div className="column column-1" style={{ flex: 1 }}></div>
                                    
                                    {/* Column 2 - Bar chart and labels */}
                                    <div className="column column-2" style={{ flex: 1 }}>
                                        
                                    <span className="label">Best Result{running ? " so far" : ""}:</span>
                                    <span className="value">{metric?.fformat(runOutput?.bestResult)}</span>
                                    <div style={{height: 40}}/>
                                    <span className="label">Generations Run:</span>
                                    {false && runOutput?.bestResultHistory?.map((d, i) => {
                                        const prevRow = i > 0 ? runOutput?.bestResultHistory[i - 1] : null
                                        const diffValue = prevRow ? d?.value - prevRow?.value : null
                                        return <span key={i} className={'history-block' + (d == selectedHistoryEntry ? ' sel' : '')} onClick={() => setSelectedHistoryEntry(d == selectedHistoryEntry ? null : d)}>
                                            {metric?.fformat(d?.value)}
                                            {diffValue !== null ? <span style={{ marginLeft: 5 }}>
                                                {`(+${metric?.fformat(diffValue)})`}
                                            </span> : null}
                                        </span>
                                    })}
                                    {selectedHistoryEntry ? <div className='selected-history-entry'>
                                        {JSON.stringify(selectedHistoryEntry.config)}
                                    </div> : null}
                                    {
                                        chartData?.length > 0 ?
                                        <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                                            <center>
                                                {/* <ResponsiveContainer height={120}> */}                                               
                                            
                                                    <BarChart
                                                        width={50 + runOutput?.bestResultHistory?.length * 20}
                                                        height={120}
                                                        margin={{ top: 20 }}
                                                        data={chartData}
                                                        onClick={(data) => handleBarClick(data)}
                                                        >
                                                    <Tooltip
                                                        labelFormatter={() => ''} // Hides the default x-axis label
                                                        formatter={(value, name, props) => {
                                                            // Safely access the payload and format the value
                                                            const generation = props?.payload?.generationIndex || 'Unknown';
                                                            const formattedValue = metric?.key === 'share'
                                                            ? d3Format(".2%")(value)
                                                            : metric?.fformat(value) || value

                                                            return [formattedValue, `Generation ${generation}`]
                                                        }}
                                                    />
                                                    <Bar dataKey="value" fill="#202020" >
                                                        <LabelList
                                                            dataKey="value"
                                                            position="insideTop"
                                                            content={({ x, y, width, height, value }) => {
                                                            const formattedValue = metric?.key === 'share' ? d3Format(".2%")(value) : metric?.fformat(value) || value;

                                                            const fontSize = 10; // Adjust as needed
                                                            const textX = x + width / 2;
                                                            const textY = y + height / 2;

                                                            return (
                                                                <text
                                                                x={textX}
                                                                y={textY}
                                                                textAnchor="middle"
                                                                fill="#fff"
                                                                style={{ fontSize }}
                                                                alignmentBaseline="middle" // Ensures vertical centering
                                                                transform={`rotate(-90, ${textX}, ${textY})`} // Rotate around the center
                                                                >
                                                                {formattedValue}
                                                                </text>
                                                            )
                                                        }}
                                                        />
                                                    </Bar>

                                                </BarChart>
                                            
                                                {/* </ResponsiveContainer> */}
                                            </center>
                                        </div>
                                        : null
                                    }
                                    </div>
                                    <div className="column column-3" style={{ flex: 1, marginLeft: 20 }}>
                                        <div style={{ flex: 1, marginLeft: 20 }}>
                                            {selectedBarData && (<>
                                                <div style={{fontSize:10, fontWeight:500, padding:10, textAlign:'left', background:'#000', color:'#fff'}}>
                                                    {`Result: ${metric?.fformat ? metric.fformat(selectedBarData?.value) : selectedBarData?.value}`}
                                                </div>
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th>Attribute</th>
                                                            <th>From</th>
                                                            <th>To</th>
                                                        </tr>
                                                    </thead>
                                                 <tbody>
                                                    {
                                                        Object.keys(selectedBarData?.config || {}).map((productKey) => (
                                                        Object.keys(selectedBarData.config[productKey] || {}).map((attribute) => {
                                                            const { from, to } = selectedBarData.config[productKey][attribute] || {}
                                                            return (
                                                            <tr key={`${productKey}-${attribute}`}>
                                                                <td>{atts?.find(a => a.name === attribute)?.label}</td>
                                                                <td>{atts?.find(a => a.name === attribute)?.levels.find(lev => lev.value === from)?.label}</td> 
                                                                <td>{atts?.find(a => a.name === attribute)?.levels.find(lev => lev.value === to)?.label}</td>
                                                            </tr>
                                                            )
                                                        })
                                                        ))
                                                    }
                                                    </tbody>

                                                </table>
                                            </>)}
                                        </div>
                                    </div>
                                    
                                </div> : null}
                            
                            </div>
                            <span className={"btn inline-block border right-space" + (run.hasOutput ? "" : " disabled")} disabled={true}
                                onClick={() => {
                                    loadOptimizerScenario(auth, studyId, run.id, 0, dispatch, (success, message) => {
                                        // navigate to what-if page
                                        window.topBar.enqueueSnack({
                                            id: '',
                                            message,
                                            to: 3000,
                                            success
                                        })
                                        
                                    })
                                    
                                    window.topBar.hideMenu()
                                    history.push(`/study/${studyId}/${scnLoadPath}`) //`/study/${studyId}/what-if`
                                    
                                }}
                            >
                                Load best Scenario
                            </span>
                            <span className={"btn inline-block border right-space" + (false && run.hasOutput ? "" : " disabled")}>Analyze top {nbScenarios} Scenarios</span>
                            <span className={"btn inline-block border right-space " + (false ? "" : " disabled")}>Load optimizer setup</span>
                            <span className={"btn inline-block border right-space" + (false && run.hasOutput ? "" : " disabled")}>Save run</span>
                        </div>
                    </div>
                        
                </>}
            </div> : null}
        </div>
    )
}

